import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import InlineInput from "@via/components/inputs/inline-input.tsx";
import Table from "@via/components/table/table.tsx";
import { type Sample } from "@via/context/file-organizer-context.tsx";
import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { type JSX } from "react";

const columnHelper = createColumnHelper<Sample>();

export default function SampleTable({
  onSampleRemove,
}: {
  onSampleRemove: (sampleId: string) => void;
}): JSX.Element {
  const { selectedSamples, updateSampleName, removeSample } =
    useFileOrganizer();

  const columns = [
    columnHelper.accessor("name", {
      cell: info => (
        <InlineInput
          value={info.getValue()}
          onBlur={name => {
            handleUpdateSampleName(info.row.original, name);
          }}
        />
      ),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("filesUsed", {
      cell: info => <div className="mx-2">{info.getValue()}</div>,
      header: () => <span className="mx-2">Files Used</span>,
    }),
    columnHelper.accessor("directory", {
      cell: info => info.getValue(),
      header: () => <span>Directory</span>,
    }),
    columnHelper.accessor("id", {
      cell: info => (
        <button
          onClick={() => handleRemoveItem(info.getValue() || "")}
          className="inline-flex break-normal items-center gap-x-1.5 rounded-md bg-red-500 px-2 py-2 text-sm text-white shadow-sm duration-200 ease-in-out hover:bg-red-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
          Remove
        </button>
      ),
      header: () => null,
    }),
  ] as Array<ColumnDef<Sample>>;

  const handleUpdateSampleName = (
    originalSample: Sample,
    newName: string
  ): void => {
    updateSampleName(originalSample, newName);
  };

  const handleRemoveItem = (itemId: string) => {
    removeSample(itemId);
    onSampleRemove(itemId);
  };

  return (
    <div className="mt-8" data-testid="sampleTable">
      <Table
        data={selectedSamples || []}
        columns={columns}
        total={(selectedSamples || []).length}
      />
    </div>
  );
}
